import Isemail from 'isemail'
import { isValidCPF } from './cpf'

// ID do Brasil na tabela `countries` do backend. CPF só é cobrado
// quando o hóspede declara Brasil como país.
export const BRAZIL_COUNTRY_ID = 31

function isBrazilian(checkin) {
  return Number(checkin.nationality) === BRAZIL_COUNTRY_ID
}

export const STEP_SLUGS = [
  'guest-info',
  'guest-photo',
  'guest-document-photo',
  'guest-questions',
  'success'
]

export function findScreenSlug(step) {
  return STEP_SLUGS[+step] || ''
}

// Tabela de validações da tela "guest-info". Cada entrada cruza o nome do
// campo no checkin_fields_behaviour com a regra que define se o valor está
// em branco — diferente para inputs livres (string vazia/undefined) e selects
// (-1 como sentinela de "não selecionado").
const GUEST_INFO_BLANK_RULES = [
  { field: 'name',          isBlank: (c) => c.name === '' },
  { field: 'surname',       isBlank: (c) => c.surname === '' },
  { field: 'email',         isBlank: (c) => c.email === '' },
  { field: 'phone',         isBlank: (c) => c.phone === '' },
  { field: 'document',      isBlank: (c) => !c.document },
  // tax_id (CPF) só é exigido para hóspedes brasileiros. Para estrangeiros,
  // a regra não dispara mesmo que o config marque `required: true` — porque
  // o conceito de CPF não se aplica.
  { field: 'tax_id',        isBlank: (c) => isBrazilian(c) && !c.tax_id },
  { field: 'date_of_birth', isBlank: (c) => !c.birth_date },
  { field: 'gender',        isBlank: (c) => c.gender < 0 },
  { field: 'address',       isBlank: (c) => c.address === '' },
  { field: 'number',        isBlank: (c) => c.number === '' },
  { field: 'complement',    isBlank: (c) => c.complement === '' },
  { field: 'neighborhood',  isBlank: (c) => c.neighborhood === '' },
  { field: 'city',          isBlank: (c) => c.city === '' },
  { field: 'state',         isBlank: (c) => c.state === '' },
  { field: 'post_code',     isBlank: (c) => c.postal_code === '' },
  { field: 'country',       isBlank: (c) => c.country < 0 },
  { field: 'nationality',   isBlank: (c) => c.nationality < 0 }
]

const QUESTIONS_BLANK_RULES = [
  { field: 'q1_origin',                    isBlank: (c) => c.q1_origin === '' },
  { field: 'q2_destination',               isBlank: (c) => c.q2_destination === '' },
  { field: 'q3_trip_motivation',           isBlank: (c) => c.q3_trip_motivation === '' },
  { field: 'q4_property_choose_motivation',isBlank: (c) => c.q4_property_choose_motivation === '' },
  { field: 'country_entry_date',           isBlank: (c) => !c.country_entry_date },
  { field: 'transport_method',             isBlank: (c) => c.transport_method === '' }
]

function isRequired(fieldsBehaviour, fieldName) {
  const entry = fieldsBehaviour.find((e) => e.field === fieldName)
  return entry ? Boolean(entry.required) : false
}

export function validateGuestInfo(fieldsBehaviour, checkin) {
  const violatesBlank = GUEST_INFO_BLANK_RULES.some(
    (rule) => isRequired(fieldsBehaviour, rule.field) && rule.isBlank(checkin)
  )
  if (violatesBlank) return false

  if (isRequired(fieldsBehaviour, 'email') && !Isemail.validate(checkin.email)) {
    return false
  }
  // Brasileiro que digitou algo no CPF tem que digitar um CPF válido,
  // mesmo que o campo não seja obrigatório. O gate de "required" governa só
  // a regra de blank — formato é checado sempre que há valor.
  if (
    isBrazilian(checkin) &&
    checkin.tax_id &&
    !isValidCPF(checkin.tax_id)
  ) {
    return false
  }
  return true
}

export function validateQuestions(fieldsBehaviour, checkin, termsAccepted) {
  if (!termsAccepted) return false
  return !QUESTIONS_BLANK_RULES.some(
    (rule) => isRequired(fieldsBehaviour, rule.field) && rule.isBlank(checkin)
  )
}

// Nomes do checkin_fields_behaviour que divergem da chave usada no state
// do checkin. A maioria casa 1:1 — só os dois abaixo precisam de tradução.
const FIELD_TO_STATE_KEY = {
  date_of_birth: 'birth_date',
  post_code: 'postal_code'
}

// Zera (para o default de blankCheckin) todo campo marcado como invisível
// no fields_behaviour. Sem isso, dev-defaults, auto-fill de reserva e
// resíduos de state vazariam para o backend mesmo quando o hóspede não viu
// o campo. Aplica antes do save.
export function stripHiddenFields(checkin, fieldsBehaviour) {
  const blank = blankCheckin(checkin.roomId)
  const result = { ...checkin }
  fieldsBehaviour.forEach((entry) => {
    if (entry.visible) return
    const key = FIELD_TO_STATE_KEY[entry.field] || entry.field
    if (Object.prototype.hasOwnProperty.call(blank, key)) {
      result[key] = blank[key]
    }
  })
  return result
}

export function blankCheckin(roomId) {
  return {
    roomId,
    name: '',
    surname: '',
    gender: '-1',
    birth_date: null,
    email: '',
    document: '',
    tax_id: '',
    phone: '',
    nationality: '-1',
    country: '-1',
    city: '-1',
    state: '-1',
    q1_origin: '',
    q2_destination: '',
    q3_trip_motivation: '',
    q4_property_choose_motivation: '',
    q5_information: '',
    country_entry_date: '',
    transport_method: '',
    photo: null,
    document_photo: null,
    address: '',
    number: '',
    complement: '',
    neighborhood: '',
    postal_code: ''
  }
}

export function applyDevDefaults(checkin) {
  const env = process.env
  const enabled =
    typeof env.REACT_APP_GUEST_LOAD_DEFAULT_DATA === 'string' &&
    env.REACT_APP_GUEST_LOAD_DEFAULT_DATA.toLowerCase() === 'yes'
  if (!enabled) return checkin
  return {
    ...checkin,
    name: env.REACT_APP_GUEST_NAME || '',
    surname: env.REACT_APP_GUEST_SURNAME || '',
    email: env.REACT_APP_GUEST_EMAIL || '',
    phone: env.REACT_APP_GUEST_PHONE_NUMBER || '',
    document: env.REACT_APP_GUEST_DOCUMENT || '',
    tax_id: env.REACT_APP_GUEST_TAX_ID || '',
    birth_date: env.REACT_APP_GUEST_BIRTH_DATE || '',
    gender: env.REACT_APP_GUEST_GENDER || checkin.gender,
    address: env.REACT_APP_GUEST_ADDRESS || '',
    number: env.REACT_APP_GUEST_NUMBER || '',
    complement: env.REACT_APP_GUEST_COMPLEMENT || '',
    neighborhood: env.REACT_APP_GUEST_NEIGHBORHOOD || '',
    country: env.REACT_APP_GUEST_COUNTRY || checkin.country,
    nationality: env.REACT_APP_GUEST_NATIONALITY || checkin.nationality,
    city: env.REACT_APP_GUEST_CITY || checkin.city,
    state: env.REACT_APP_GUEST_STATE || checkin.state,
    postal_code: env.REACT_APP_GUEST_ZIPCODE || ''
  }
}
